import { AsyncPipe } from '@angular/common'
import { AfterViewInit, Component, inject, OnInit, signal, ViewChild } from '@angular/core'
import { RouterLink } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { PrimeTemplate } from 'primeng/api'
import { Button } from 'primeng/button'
import { Dialog } from 'primeng/dialog'
import { Fluid } from 'primeng/fluid'
import { InputText } from 'primeng/inputtext'
import { takeUntil } from 'rxjs'

import { MfaMethod } from '@ti/contracts'
import { isAdminRole } from '@ti/core/auth/utils'
import { injectDestroy$, selectState } from '@ti/core/common/utils'
import { CONFIG } from '@ti/core/contracts/config'
import { PROFILE } from '@ti/core/contracts/profile'
import { PUSH_CLIENT } from '@ti/core/contracts/push'
import { LanguageService } from '@ti/core/ui-kit/i18n'
import { AvatarComponent } from '@ti/core/ui-kit/layout/components/avatar/avatar.component'
import { TooltipDirective } from '@ti/core/ui-kit/layout/directives'
import { AuthenticatorSetupDialogComponent } from '@ti/shared/auth-ui/components/authenticator-setup-dialog'
import { MfaClient } from '@ti/shared/auth-ui/services/mfa.client'

import { BaseUserSettingsModel } from '../../models/base-user-settings.model'

@Component({
  selector: 'app-user-settings-dialog',
  templateUrl: 'user-settings-dialog.component.html',
  styleUrls: ['user-settings-dialog.component.scss'],
  imports: [
    AsyncPipe,
    AuthenticatorSetupDialogComponent,
    AvatarComponent,
    Button,
    Dialog,
    Fluid,
    InputText,
    PrimeTemplate,
    RouterLink,
    TranslatePipe,
    TooltipDirective,
  ],
})
export class UserSettingsDialogComponent implements OnInit, AfterViewInit {
  protected readonly config = inject(CONFIG)
  protected readonly languageService = inject(LanguageService)
  protected readonly mfaClient = inject(MfaClient)
  protected readonly model = inject(BaseUserSettingsModel)
  protected readonly profile = inject(PROFILE)
  protected readonly pushes = inject(PUSH_CLIENT)
  protected readonly state = selectState(BaseUserSettingsModel)
  protected readonly destroy$ = injectDestroy$()

  protected readonly isOpened = signal(false)
  protected readonly isPushNotificationsAvailable = signal(false)
  protected readonly isTotpMfaEnabled = signal(false)
  protected readonly isDisableMfaProcessing = signal(false)
  protected readonly strictMfaEnforcementMode = signal(false)

  protected readonly PASSWORD_PLACEHOLDER = '****************'

  @ViewChild('authSetupDialogRef')
  protected readonly authSetupDialogRef!: AuthenticatorSetupDialogComponent

  public open() {
    this.isOpened.set(true)
  }

  public close() {
    this.isOpened.set(false)
  }

  public async ngOnInit() {
    this.isPushNotificationsAvailable.set(!!this.config.fcmVapidKey)
    await this.model.init()
  }

  public async ngAfterViewInit() {
    this.profile.state.pipe(takeUntil(this.destroy$)).subscribe((profile) => {
      if (!profile) return

      // Force setup & disable unassigning 2FA for TI Admins (for prod only)
      this.strictMfaEnforcementMode.set(
        this.config.envType === 'production' && isAdminRole(profile.roleId),
      )

      const isTotpMfaEnabled = !!profile.mfaStatus?.includes(MfaMethod.TOTP)

      this.isTotpMfaEnabled.set(isTotpMfaEnabled)
      const requiresMFA = isAdminRole(profile.roleId) && !isTotpMfaEnabled
      if (requiresMFA) {
        this.authSetupDialogRef.open()
      }
    })
  }

  public async disableMfa() {
    this.isDisableMfaProcessing.set(true)
    try {
      await this.mfaClient.disableSoftwareToken()
    } catch (e) {
      console.log('Cannot disable MFA', e)
    } finally {
      this.isDisableMfaProcessing.set(false)
    }
  }

  public async enablePushes() {
    this.model.setErrorMessage('')

    const isSuccess = (await this.pushes.register()) && (await this.pushes.isRegistered())
    if (!isSuccess) {
      this.model.setErrorMessage(
        await this.languageService.translate('users.profile.pushes-denied-summary'),
      )
    }
  }

  protected readonly console = console
}
